import {
  clamp,
  columnBounds,
  photoColumnsOf,
  rng,
  type PhotoWindow,
  type SetStamp,
  type StripGeometry
} from './layoutSet'

/** Where a face usually is in a photo window, as shares of the window. */
const FACE = { cx: 0.5, cy: 0.42, rx: 0.3, ry: 0.32 }

/** Copies tilt up to this many degrees either way, and are never turned over. */
export const AUTO_MAX_TILT = 24

/** A copy's width as a share of its photograph, before the art's scale and jitter. */
const BASE_WIDTH = 0.36

/** Kept clear of the strip's outer edge, as a share of the canvas. */
const EDGE = 0.012

/** How far a copy may wander from its spot, as a share of the window, either way. */
const WANDER = 0.05

interface Zone {
  key: string
  x: number
  y: number
  weight: number
}

/**
 * The spots around a photograph a copy may sit on, as shares of the window.
 *
 * Each is ON the window's edge, so a copy overhangs onto the frame rather than
 * covering the photograph, and none is near the middle, where faces are.
 * Corners are picked most often; the bottom edge and the lower sides now and
 * then, so a strip does not read as four identical corner badges.
 */
const ZONES: Zone[] = [
  { key: 'tl', x: 0.06, y: 0.07, weight: 3 },
  { key: 'tr', x: 0.94, y: 0.07, weight: 3 },
  { key: 'bl', x: 0.06, y: 0.93, weight: 3 },
  { key: 'br', x: 0.94, y: 0.93, weight: 3 },
  { key: 'b', x: 0.5, y: 0.97, weight: 1.5 },
  { key: 'l', x: 0.02, y: 0.8, weight: 1 },
  { key: 'r', x: 0.98, y: 0.8, weight: 1 }
]

/**
 * Lay copies of ONE sticker over a strip for the guest: Auto layout.
 *
 * About one copy per photograph (two each when the strip has only one or two),
 * every copy its own size and tilt, on a spot picked at random around its
 * photograph's edge. A spot is taken only if the copy stays clear of every
 * face and of the copies already down; when no spot is clear the least crowded
 * one is used, so a strip is never left short.
 *
 * Copies stay inside their own strip column: a two-strip sheet is cut down the
 * middle, and a copy across the cut would print in halves.
 *
 * Deterministic in `seed`: the preview the guest approves is the print, and a
 * new seed is a genuinely new arrangement (see `autoLayoutSignature`).
 *
 * @param artRatio the sticker's height / width.
 * @param scale from `setScale`, so sparse and solid art weigh the same.
 */
export function layoutAuto(
  geometry: StripGeometry | undefined,
  artRatio: number,
  seed: string,
  scale = 1
): SetStamp[] {
  const columns = photoColumnsOf(geometry)
  if (!geometry || columns.length === 0) return []

  const W = geometry.canvasWidth
  const H = geometry.canvasHeight
  const random = rng(seed)
  const windows = columns.flat()
  const perWindow = windows.length <= 2 ? 2 : 1
  const bounds = columnBounds(columns, W)
  const placed: { x: number; y: number; r: number }[] = []
  const stamps: SetStamp[] = []

  columns.forEach((column, index) => {
    const { left, right } = bounds[index]
    let previousZone: string | null = null

    for (const win of column) {
      const usedHere: string[] = []
      for (let copy = 0; copy < perWindow; copy++) {
        const basis = Math.min(win.width, win.height * 1.25)
        const width = BASE_WIDTH * scale * basis * (0.85 + 0.3 * random())
        const height = width * artRatio
        // Art rarely fills its box corner to corner, so the clearance circle is
        // a little inside the box's own diagonal.
        const radius = (Math.hypot(width, height) / 2) * 0.8

        const candidates = weightedOrder(
          ZONES.filter((z) => z.key !== previousZone && !usedHere.includes(z.key)),
          random
        )
        let best: { x: number; y: number; key: string; score: number } | null = null
        for (const zone of candidates) {
          const x = clamp(
            win.left + (zone.x + (random() * 2 - 1) * WANDER) * win.width,
            left + EDGE * W + width / 2,
            right - EDGE * W - width / 2
          )
          const y = clamp(
            win.top + (zone.y + (random() * 2 - 1) * WANDER) * win.height,
            EDGE * H + height / 2,
            H - EDGE * H - height / 2
          )
          const onFace = windows.some((w) => overlapsFace(w, x, y, radius * 0.7))
          const crowding = Math.min(
            0,
            ...placed.map((p) => Math.hypot(p.x - x, p.y - y) - (p.r + radius))
          )
          const score = (onFace ? -1e6 : 0) + crowding
          if (!best || score > best.score) best = { x, y, key: zone.key, score }
          if (score === 0) break
        }
        if (!best) continue

        stamps.push({
          xPct: (best.x / W) * 100,
          yPct: (best.y / H) * 100,
          widthPct: (width / W) * 100,
          rotation: (random() * 2 - 1) * AUTO_MAX_TILT
        })
        placed.push({ x: best.x, y: best.y, r: radius })
        usedHere.push(best.key)
        previousZone = best.key
      }
    }
  })

  return stamps
}

/**
 * A coarse fingerprint of where the copies sit, to tell two arrangements apart.
 *
 * Shuffle compares the new fingerprint with the one on screen and draws again
 * when they match, so the dice always visibly changes the strip.
 */
export function autoLayoutSignature(stamps: SetStamp[]): string {
  return stamps.map((s) => `${Math.round(s.xPct / 5)}:${Math.round(s.yPct / 5)}`).join('|')
}

/** Whether a copy centred at (x, y), `r` across, would cover the window's face. */
function overlapsFace(win: PhotoWindow, x: number, y: number, r: number): boolean {
  const cx = win.left + FACE.cx * win.width
  const cy = win.top + FACE.cy * win.height
  const rx = FACE.rx * win.width + r
  const ry = FACE.ry * win.height + r
  return ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1
}

/** The zones in a random order, heavier ones more likely to come first. */
function weightedOrder(zones: Zone[], random: () => number): Zone[] {
  const pool = [...zones]
  const order: Zone[] = []
  while (pool.length > 0) {
    const total = pool.reduce((sum, z) => sum + z.weight, 0)
    let pick = random() * total
    let i = 0
    while (i < pool.length - 1 && pick >= pool[i].weight) {
      pick -= pool[i].weight
      i++
    }
    order.push(pool.splice(i, 1)[0])
  }
  return order
}
